// Schema.org JSON-LD builders — a single source of truth for structured data.
// Consumed by <JsonLd> component which injects <script type="application/ld+json">.
//
// This module is written for maximum discoverability by:
//   - Google / Bing / Yahoo / DuckDuckGo (SEO)
//   - Voice assistants + Speakable (Google Assistant, Siri)
//   - Answer engines (Perplexity, You, Neeva-alikes) — AEO
//   - AI generative crawlers (GPTBot, ClaudeBot, PerplexityBot, Google-Extended, Bytespider) — GEO
//
// Entities are interconnected via `@id` so crawlers see a single knowledge graph.

import { CLINIC_NAP } from "@/data/cities";

const SITE = CLINIC_NAP.url;

// Canonical @ids — used across all builders to interlink entities into a graph.
const PHYSICIAN_ID = `${SITE}/#physician`;
const ORGANIZATION_ID = `${SITE}/#organization`;
const LOCALBUSINESS_ID = `${SITE}/#localbusiness`;
const WEBSITE_ID = `${SITE}/#website`;
const LOGO_URL = `${SITE}/logo.png`;
const HERO_PORTRAIT =
  "https://customer-assets-v7afamib.emergentagent.net/job_vascular-specialist/artifacts/h42j5vhh__DSC9521.webp";
const ABOUT_PORTRAIT =
  "https://customer-assets-v7afamib.emergentagent.net/job_vascular-specialist/artifacts/tnxod6go__DSC9518.webp";

// -----------------------------------------------------------------------------
// Physician — the primary entity. Enriched heavily for AI/answer-engine citation.
// -----------------------------------------------------------------------------
export function physicianJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": ["Physician", "Person"],
    "@id": PHYSICIAN_ID,
    "name": "Dr Tegbir Singh Sidhu",
    "givenName": "Tegbir Singh",
    "familyName": "Sidhu",
    "honorificPrefix": "Dr",
    "jobTitle": "Principal Consultant — Vascular & Endovascular Interventional Radiology",
    "description":
      "Dr Tegbir Singh Sidhu is a senior Interventional Radiologist practising vascular and endovascular interventional radiology at Max Super Speciality Hospital, Mohali. He performs minimally invasive image-guided treatments for aortic aneurysms, peripheral artery disease, deep vein thrombosis, uterine fibroids, prostate embolisation, dialysis access, and interventional oncology, and is known for continuity of care from first WhatsApp message to final follow-up.",
    "image": [ABOUT_PORTRAIT, HERO_PORTRAIT],
    "url": `${SITE}/about`,
    "mainEntityOfPage": { "@id": `${SITE}/about#page` },
    "telephone": CLINIC_NAP.telephone,
    "email": CLINIC_NAP.email,
    "gender": "Male",
    "nationality": { "@type": "Country", "name": "India" },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": CLINIC_NAP.streetAddress,
      "addressLocality": CLINIC_NAP.addressLocality,
      "addressRegion": CLINIC_NAP.addressRegion,
      "postalCode": CLINIC_NAP.postalCode,
      "addressCountry": CLINIC_NAP.addressCountry,
    },
    "workLocation": { "@id": LOCALBUSINESS_ID },
    "worksFor": { "@id": ORGANIZATION_ID },
    "affiliation": {
      "@type": "Hospital",
      "name": "Max Super Speciality Hospital, Mohali",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Mohali",
        "addressRegion": "Punjab",
        "addressCountry": "IN",
      },
    },
    "medicalSpecialty": [
      "https://schema.org/InterventionalRadiology",
      "https://schema.org/DiagnosticRadiology",
      "InterventionalRadiology",
      "VascularSurgery",
    ],
    "knowsAbout": [
      "Interventional Radiology",
      "Endovascular Aneurysm Repair (EVAR)",
      "Thoracic Endovascular Aortic Repair (TEVAR)",
      "Peripheral Angioplasty and Stenting",
      "Critical Limb Ischaemia and Limb Salvage",
      "Diabetic Foot Angioplasty",
      "Deep Vein Thrombosis — Thrombolysis and Venous Stenting",
      "Varicose Vein Endovenous Laser and RFA",
      "Uterine Fibroid Embolisation (UFE / UAE)",
      "Prostate Artery Embolisation (PAE)",
      "Transarterial Chemoembolisation (TACE) for Liver Cancer",
      "Yttrium-90 Radioembolisation",
      "Percutaneous Tumour Ablation (Kidney, Liver, Lung)",
      "Dialysis Access — AV Fistula Creation and Salvage",
      "Carotid Stenting",
      "Interventional Oncology",
      "Second-Opinion Imaging Review for NRI Patients",
    ],
    "hasCredential": [
      { "@type": "EducationalOccupationalCredential", "credentialCategory": "degree", "name": "MBBS" },
      { "@type": "EducationalOccupationalCredential", "credentialCategory": "degree", "name": "MD Radiology" },
      { "@type": "EducationalOccupationalCredential", "credentialCategory": "fellowship", "name": "Fellowship in Vascular & Interventional Radiology" },
      { "@type": "EducationalOccupationalCredential", "credentialCategory": "certification", "name": "Registered with Punjab Medical Council" },
    ],
    "memberOf": [
      { "@type": "MedicalOrganization", "name": "Indian Society of Vascular & Interventional Radiology (ISVIR)" },
      { "@type": "MedicalOrganization", "name": "Cardiovascular and Interventional Radiological Society of Europe (CIRSE)" },
      { "@type": "MedicalOrganization", "name": "Indian Radiological & Imaging Association (IRIA)" },
      { "@type": "MedicalOrganization", "name": "Punjab Medical Council" },
      { "@type": "MedicalOrganization", "name": "Medical Council of India" },
    ],
    "availableService": [
      { "@type": "MedicalProcedure", "name": "Endovascular Aortic Aneurysm Repair (EVAR/TEVAR)", "procedureType": "https://schema.org/PercutaneousProcedure" },
      { "@type": "MedicalProcedure", "name": "Peripheral Angioplasty and Stenting", "procedureType": "https://schema.org/PercutaneousProcedure" },
      { "@type": "MedicalProcedure", "name": "Uterine Fibroid Embolisation (UFE/UAE)", "procedureType": "https://schema.org/PercutaneousProcedure" },
      { "@type": "MedicalProcedure", "name": "Prostate Artery Embolisation (PAE)", "procedureType": "https://schema.org/PercutaneousProcedure" },
      { "@type": "MedicalProcedure", "name": "Deep Vein Thrombosis Thrombolysis", "procedureType": "https://schema.org/PercutaneousProcedure" },
      { "@type": "MedicalProcedure", "name": "Endovenous Laser & RFA for Varicose Veins", "procedureType": "https://schema.org/PercutaneousProcedure" },
      { "@type": "MedicalProcedure", "name": "Transarterial Chemoembolisation (TACE)", "procedureType": "https://schema.org/PercutaneousProcedure" },
      { "@type": "MedicalProcedure", "name": "Yttrium-90 Radioembolisation", "procedureType": "https://schema.org/PercutaneousProcedure" },
      { "@type": "MedicalProcedure", "name": "AV Fistula Creation & Salvage for Dialysis", "procedureType": "https://schema.org/PercutaneousProcedure" },
      { "@type": "MedicalProcedure", "name": "Percutaneous Tumour Ablation", "procedureType": "https://schema.org/PercutaneousProcedure" },
      { "@type": "MedicalProcedure", "name": "Carotid Stenting", "procedureType": "https://schema.org/PercutaneousProcedure" },
      { "@type": "MedicalProcedure", "name": "Second-Opinion Written Imaging Review", "procedureType": "https://schema.org/NoninvasiveProcedure" },
    ],
    "areaServed": [
      { "@type": "AdministrativeArea", "name": "Chandigarh" },
      { "@type": "AdministrativeArea", "name": "Punjab" },
      { "@type": "AdministrativeArea", "name": "Haryana" },
      { "@type": "AdministrativeArea", "name": "Himachal Pradesh" },
      { "@type": "AdministrativeArea", "name": "Jammu and Kashmir" },
      { "@type": "AdministrativeArea", "name": "Delhi NCR" },
      { "@type": "Country", "name": "Canada" },
      { "@type": "Country", "name": "United States" },
      { "@type": "Country", "name": "United Kingdom" },
      { "@type": "Country", "name": "Australia" },
      { "@type": "Country", "name": "United Arab Emirates" },
      { "@type": "Country", "name": "Saudi Arabia" },
    ],
    "sameAs": [
      CLINIC_NAP.gbpUrl,
      "https://www.isvir.org",
      "https://www.cirse.org",
    ].filter(Boolean),
  };
}

// -----------------------------------------------------------------------------
// MedicalOrganization — the practice as an entity (distinct from physical clinic).
// -----------------------------------------------------------------------------
export function medicalOrganizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "MedicalOrganization",
    "@id": ORGANIZATION_ID,
    "name": "Dr Tegbir Vascular — Interventional Radiology",
    "alternateName": ["Dr Tegbir Sidhu Vascular", "Tegbir Sidhu IR Practice"],
    "url": SITE,
    "logo": LOGO_URL,
    "image": HERO_PORTRAIT,
    "telephone": CLINIC_NAP.telephone,
    "email": CLINIC_NAP.email,
    "founder": { "@id": PHYSICIAN_ID },
    "employee": { "@id": PHYSICIAN_ID },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": CLINIC_NAP.streetAddress,
      "addressLocality": CLINIC_NAP.addressLocality,
      "addressRegion": CLINIC_NAP.addressRegion,
      "postalCode": CLINIC_NAP.postalCode,
      "addressCountry": CLINIC_NAP.addressCountry,
    },
    "medicalSpecialty": [
      "InterventionalRadiology",
      "VascularSurgery",
      "DiagnosticRadiology",
    ],
    "sameAs": [CLINIC_NAP.gbpUrl].filter(Boolean),
  };
}

// -----------------------------------------------------------------------------
// LocalBusiness / MedicalBusiness — the physical clinic. Used on city + contact pages.
// -----------------------------------------------------------------------------
export function localBusinessJsonLd(overrides = {}) {
  return {
    "@context": "https://schema.org",
    "@type": ["MedicalBusiness", "LocalBusiness"],
    "@id": LOCALBUSINESS_ID,
    "name": CLINIC_NAP.name,
    "url": SITE,
    "image": HERO_PORTRAIT,
    "logo": LOGO_URL,
    "telephone": CLINIC_NAP.telephone,
    "email": CLINIC_NAP.email,
    "openingHours": CLINIC_NAP.openingHours,
    "priceRange": "₹₹",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": CLINIC_NAP.streetAddress,
      "addressLocality": CLINIC_NAP.addressLocality,
      "addressRegion": CLINIC_NAP.addressRegion,
      "postalCode": CLINIC_NAP.postalCode,
      "addressCountry": CLINIC_NAP.addressCountry,
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": CLINIC_NAP.latitude,
      "longitude": CLINIC_NAP.longitude,
    },
    "hasMap": CLINIC_NAP.gbpShareUrl || CLINIC_NAP.gbpUrl,
    "medicalSpecialty": "InterventionalRadiology",
    "parentOrganization": { "@id": ORGANIZATION_ID },
    "employee": { "@id": PHYSICIAN_ID },
    "sameAs": [CLINIC_NAP.gbpUrl].filter(Boolean),
    ...overrides,
  };
}

// -----------------------------------------------------------------------------
// WebSite — enables Google Sitelinks Searchbox + declares site publisher.
// -----------------------------------------------------------------------------
export function websiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    "url": SITE,
    "name": "Dr Tegbir Vascular",
    "alternateName": "Dr Tegbir Sidhu — Vascular & Endovascular IR",
    "publisher": { "@id": ORGANIZATION_ID },
    "inLanguage": "en-IN",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${SITE}/care?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

// -----------------------------------------------------------------------------
// MedicalWebPage — used on hub, condition, city, journal pages.
// Adds Speakable spec for voice-assistant / AI-crawler friendliness.
// -----------------------------------------------------------------------------
export function medicalWebPageJsonLd({ name, description, url, about, lastReviewed, speakable = true }) {
  const node = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    "name": name,
    "description": description,
    "url": `${SITE}${url}`,
    "inLanguage": "en-IN",
    "isPartOf": { "@id": WEBSITE_ID },
    "publisher": { "@id": ORGANIZATION_ID },
    "author": { "@id": PHYSICIAN_ID },
    "about": about ? { "@type": "MedicalCondition", "name": about } : undefined,
    "lastReviewed": lastReviewed || new Date().toISOString().slice(0, 10),
    "reviewedBy": { "@id": PHYSICIAN_ID },
    "audience": { "@type": "MedicalAudience", "audienceType": "Patient" },
    "primaryImageOfPage": { "@type": "ImageObject", "url": HERO_PORTRAIT },
  };
  if (speakable) {
    node.speakable = {
      "@type": "SpeakableSpecification",
      "cssSelector": ["h1", ".speakable-summary", ".kbd + h2", "article p:first-of-type"],
    };
  }
  return node;
}

// -----------------------------------------------------------------------------
// FAQPage — the workhorse for AEO/GEO. Every FAQ block should feed this.
// -----------------------------------------------------------------------------
export function faqPageJsonLd(faqs) {
  if (!Array.isArray(faqs) || faqs.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": f.a,
        "author": { "@id": PHYSICIAN_ID },
      },
    })),
  };
}

// -----------------------------------------------------------------------------
// BreadcrumbList — always include on every non-root page.
// -----------------------------------------------------------------------------
export function breadcrumbListJsonLd(trail = []) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": trail.map((t, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": t.label,
      "item": t.to ? `${SITE}${t.to}` : undefined,
    })),
  };
}

// -----------------------------------------------------------------------------
// MedicalProcedure / Service — for condition-page and procedure detail schema.
// -----------------------------------------------------------------------------
export function medicalProcedureJsonLd({ name, description, url, bodyLocation, alsoKnownAs, howPerformed, followup }) {
  return {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    "name": name,
    "alternateName": alsoKnownAs,
    "description": description,
    "url": `${SITE}${url}`,
    "procedureType": "https://schema.org/PercutaneousProcedure",
    "bodyLocation": bodyLocation,
    "howPerformed": howPerformed,
    "followup": followup,
    "performer": { "@id": PHYSICIAN_ID },
  };
}

// -----------------------------------------------------------------------------
// HowTo — perfect for the How It Works page (steps → HowToStep).
// -----------------------------------------------------------------------------
export function howToJsonLd({ name, description, steps, url }) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": name,
    "description": description,
    "url": `${SITE}${url}`,
    "step": steps.map((s, i) => ({
      "@type": "HowToStep",
      "position": i + 1,
      "name": s.name,
      "text": s.text,
    })),
  };
}

// -----------------------------------------------------------------------------
// Article — for The Journal case-studies (also used by journal store publisher).
// -----------------------------------------------------------------------------
export function articleJsonLd({ headline, description, url, image, datePublished, dateModified, keywords }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": headline,
    "description": description,
    "url": `${SITE}${url}`,
    "image": image ? [image] : [HERO_PORTRAIT],
    "datePublished": datePublished,
    "dateModified": dateModified || datePublished,
    "keywords": keywords,
    "inLanguage": "en-IN",
    "isPartOf": { "@id": WEBSITE_ID },
    "author": { "@id": PHYSICIAN_ID },
    "publisher": { "@id": ORGANIZATION_ID },
    "mainEntityOfPage": `${SITE}${url}`,
  };
}
